import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../../App";
import axios from "axios";
import { 
  LayoutDashboard, Heart, Gift, Users, 
  LogOut, Menu, X, Settings, BarChart2, Ticket 
} from "lucide-react";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/admin/weddings", icon: Heart, label: "Weddings" },
    { path: "/admin/raffle", icon: Ticket, label: "Raffle" },
    { path: "/admin/artifacts", icon: Gift, label: "Artifacts" },
    { path: "/admin/users", icon: Users, label: "Users" },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-wedding-black">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-wedding-charcoal border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 text-wedding-ivory"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-serif text-wedding-gold">Admin</span>
        <button onClick={handleLogout} className="p-2 text-wedding-ivory/60">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="p-6">
          {/* Logo */}
          <div className="mb-8">
            <Link to="/" className="block">
              <h1 className="font-serif text-2xl text-wedding-gold">MemoraOrbs</h1>
              <p className="text-wedding-ivory/40 text-xs italic">Admin Dashboard</p>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors duration-200 ${
                  isActive(item.path, item.exact)
                    ? "bg-wedding-gold/10 text-wedding-gold"
                    : "text-wedding-ivory/60 hover:text-wedding-ivory hover:bg-white/5"
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="absolute bottom-6 left-6 right-6">
            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center gap-3 mb-4">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-wedding-gold/20 flex items-center justify-center">
                    <span className="text-wedding-gold font-bold">{user?.name?.[0]}</span>
                  </div>
                )}
                <div>
                  <p className="text-wedding-ivory text-sm">{user?.name}</p>
                  <p className="text-wedding-gold text-xs uppercase">{user?.role?.replace("_", " ")}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-wedding-ivory/60 hover:text-wedding-ivory transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Close Button */}
        <button 
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-4 right-4 p-2 text-wedding-ivory"
        >
          <X className="w-6 h-6" />
        </button>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="dashboard-content pt-16 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
